import { TaskUrgency } from '@mimir/common/async/taskPriority';
import { PrioritySemaphore, makePriority } from '@mimir/common/async/prioritySemaphore';
import { TransferStateDto, type MonitoredTransfer } from '@mimir/common/dto/transferRequestDtos';
import { PREMIERE_STANDALONE_STORAGE_ID } from '@mimir/common/util/constants';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { getTransferRequests } from '../util/api';
import { isInPremiere, type ApplicationContext, isInElectron, isInBrowser } from '@mimir/common/context/applicationContext';
import { exceptionToString } from '@mimir/common/util/exception';
import { DEFAULT_DATE_PROVIDER } from '@mimir/common/util/dateProvider';
import type { Logger } from '@mimir/common/types/logger';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import { getExternalSmTransferMonitor } from './externalSmTransferMonitorPolling';
import { showTransferCompleteToast } from '../util/showTransferCompleteToast';


async function processTransfers(
  services: {
    applicationContext: ApplicationContext,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
  },
  transfers: MonitoredTransfer[],
  storageId: string,
) {
  services.ongoingDownloadOps.mapDownloadProgress(transfers);

  // Id is more reliable than odId to find the transfer coming from server because it does not depend on artifactReference.
  const transfersMap: Record<string, MonitoredTransfer> = keyBy(transfers, 'id');

  /**
   * Remove finished transfers, if they are not in the server provided transfer list
   * then we are assuming they are finished
   */
  const odIds = services.ongoingDownloadOps.getOngoingDownloadKeys();
  for (const odId of odIds) {
    const ongoingDownload = services.ongoingDownloadOps.getOngoingDownload(odId);
    if (!ongoingDownload || ongoingDownload.yetToBeSentToServer || !ongoingDownload.id) {
      // don't clean up the ones that are just placeholder for the UI to reflect state but not sent to server yet
      continue;
    }

    if (transfersMap[ongoingDownload.id]) {
      continue;
    }

    if (isInPremiere(services.applicationContext) || isInElectron(services.applicationContext)) {
      if (getExternalSmTransferMonitor(storageId, services.applicationContext, services.logger, DEFAULT_DATE_PROVIDER).isTracking(ongoingDownload.id)) {
        continue;
      }
    } else if (isInBrowser(services.applicationContext)) {
      showTransferCompleteToast(services.applicationContext, ongoingDownload, undefined);
    }

    services.ongoingDownloadOps.deleteFromOngoingDownloads(odId);
    for (const reporter of ongoingDownload.reporters ?? []) {
      reporter.onSuccess();
    }
  }
}



const POLL_DURATION = 5;
let transferProgressTimerId: any = null;
const pollingSemaphore = new PrioritySemaphore(1);


export function startPollingTransferProgress(
  services: {
    applicationContext: ApplicationContext,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
  },
  storageId: string,
) {
  if (storageId === PREMIERE_STANDALONE_STORAGE_ID) {
    return;
  }
  if (transferProgressTimerId) {
    services.logger.debug(`Already polling a transfers' progress for: ${storageId}`);
  } else {
    services.logger.debug(`Starting download progress polling for: ${storageId}`);
    transferProgressTimerId = setInterval(() => void pollAndProcessTransferProgressSingly(services, storageId), POLL_DURATION * 1000);
  }
}


async function pollAndProcessTransferProgressSingly(
  services: {
    applicationContext: ApplicationContext,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
  },
  storageId: string,
) {
  await pollingSemaphore.limit(() => pollAndProcessTransferProgress(services, storageId), makePriority({ urgency: TaskUrgency.LongRunning }), null);
}


async function pollAndProcessTransferProgress(
  services: {
    applicationContext: ApplicationContext,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
  },
  storageId: string,
) {
  try {
    const transfers = await getTransferRequests(storageId, true);
    await processTransfers(services, transfers, storageId);
    if (canPollingBeStopped(transfers)) {
      stopPollingTransferProgress(services.logger, storageId);
    }
  } catch (error) {
    services.logger.error(`Error in pollAndProcessTransferProgress. storage: ${storageId} ${exceptionToString(error)}`);
  }
}


export function stopPollingTransferProgress(logger: Logger, storageId: string = '') {
  logger.debug(`Stopping transfer progress polling: %c${JSON.stringify(storageId)}`);
  clearInterval(transferProgressTimerId);
  transferProgressTimerId = undefined;
}


function canPollingBeStopped(ongoingDownloads: MonitoredTransfer[]) {
  return isEmpty(ongoingDownloads) ||
         isEmpty(ongoingDownloads.filter(od => od.transferState !== TransferStateDto.ERROR));
}

